/**
 * <bp-units-toggle> — measurement-unit switch for the blueprint chrome.
 *
 * Toggles between `metric` and `imperial`. Persists the choice via:
 *   1. `localStorage[smartpluvia-units]` — picked up by getUnits()
 *   2. `document.cookie[smartpluvia-units]` — picked up by UnitsHelper on SSR
 *   3. `PATCH /api/v1/users/me/preferences` — for signed-in users, so PDF
 *      export and brands SSR catalog spec follow the new choice on the next
 *      server render.
 *
 * After persisting, reloads the page so every SSR + JS surface picks up the
 * new unit system in one shot — the simplest UX since a partial swap would
 * leave server-rendered labels stale until next nav.
 */

import { getUnits, setUnits, type UnitSystem } from '../shared/units.ts';
import { updatePreferences } from '../shared/preferences.ts';
import { isAuthenticated } from '../shared/auth.ts';

const COOKIE_NAME = 'smartpluvia-units';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function writeCookie(value: UnitSystem) {
  try {
    document.cookie = `${COOKIE_NAME}=${value}; max-age=${COOKIE_MAX_AGE}; path=/; samesite=lax`;
  } catch {
    // unavailable (sandboxed iframe) — localStorage fallback still works
  }
}

export class BpUnitsToggle extends HTMLElement {
  private button: HTMLButtonElement | null = null;
  private label: HTMLElement | null = null;

  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('.bp-units-toggle-label');
    if (!this.button) return;
    this.button.addEventListener('click', this.onClick);
    this.syncLabel();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  private onClick = () => {
    const next: UnitSystem = getUnits() === 'imperial' ? 'metric' : 'imperial';
    this.apply(next);
  };

  private apply(units: UnitSystem) {
    setUnits(units);
    writeCookie(units);
    this.syncLabel();
    this.dispatchEvent(
      new CustomEvent('bp-units-change', { detail: { units }, bubbles: true }),
    );

    // Reload so SSR surfaces (brands catalog, PDF preview, planner stats
    // hydrated from server-rendered labels) pick up the new units. A
    // smaller-grain re-render is possible but pragmatically heavier.
    if (!isAuthenticated()) {
      window.location.reload();
      return;
    }

    // For a signed-in user the server renders the *saved* preference ahead
    // of the cookie (UnitsHelper#current_units), so reloading before the
    // PATCH lands re-renders the old units — and leaving the page can
    // cancel the request altogether.
    updatePreferences({ units })
      .catch(() => {
        // backend sync failed — local choice still applies; ignore.
      })
      .finally(() => window.location.reload());
  }

  private syncLabel() {
    if (!this.label || !this.button) return;
    const u = getUnits();
    this.label.textContent = u === 'imperial' ? 'ft' : 'm';
    this.button.setAttribute('aria-pressed', u === 'imperial' ? 'true' : 'false');
    this.button.dataset.units = u;
  }
}

if (!customElements.get('bp-units-toggle')) {
  customElements.define('bp-units-toggle', BpUnitsToggle);
}
